/**
 * API Client with automatic authentication
 * Handles auth headers, error responses, and base URL configuration
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/**
 * Get auth token - delegates to AuthService to avoid duplication
 */
const getAuthToken = () => {
  // Import dynamically to avoid circular dependency
  const AuthService = require('./auth').default;
  const token = AuthService.getSessionToken();
  return token;
};

/**
 * Main API client - automatically adds auth headers
 */
class ApiClient {
  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Make an authenticated request
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    // `authToken` is a per-call Authorization override (NOT a fetch option) —
    // peel it off so it never leaks into the RequestInit. Used for the few
    // sibling APIs that authenticate with a PROJECT token instead of the
    // user session token (e.g. the project-token-scoped /datasets registry).
    const { authToken, ...fetchOptions } = options;

    // Automatically add auth header
    const headers = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    const token = authToken || getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge options
    const config = {
      ...fetchOptions,
      headers,
    };

    // If body is an object, stringify it
    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      if (!response.ok) {
        const body = isJson ? await response.json() : { message: response.statusText };
        // Preserve `.message` for legacy callers (read err.message) while
        // attaching `.status` + `.body` so newer callers can branch on
        // HTTP status and read structured error payloads (validationErrors,
        // quotaInfo, missing[], etc.) without re-parsing.
        const err = new Error(body.message || body.error || `HTTP ${response.status}`);
        err.status = response.status;
        err.body = body;
        throw err;
      }

      // Return parsed JSON or raw response
      return isJson ? await response.json() : response;
    } catch (error) {
      console.error(`API Error [${options.method || 'GET'} ${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }
}

// Factory for building an ApiClient pointed at a sibling host (e.g. the
// workflow-logs subdomain RestApi). Keeps the auth-header injection logic
// shared instead of re-implementing fetch + token handling per service.
const ApiClientFactory = (baseUrl) => new ApiClient(baseUrl);

// Export singleton instances
const api = new ApiClient();

const ACCOUNT_API_BASE_URL = process.env.REACT_APP_ACCOUNT_API_BASE_URL || API_BASE_URL;
const accountApi = new ApiClient(ACCOUNT_API_BASE_URL);

// LLM-billing + Notion connectors live on a SEPARATE RestApi
// (LlmBillingRoutesNestedStack — execute-api URL distinct from the main
// api-prod.zibby.app). Local dev reuses the same Express server on :3001
// so it falls back to the main API_BASE_URL when the override is unset.
//
// Set REACT_APP_LLM_BILLING_API_URL at build time for prod. The prod
// execute-api URL (no custom domain yet) is captured here as the
// fallback for prod builds that haven't yet been re-built with the env
// var; once the deploy script wires the env var, this fallback can be
// removed.
//
// TODO(prod-env): wire REACT_APP_LLM_BILLING_API_URL into
//   backend/package.json's `build:frontend:prod` script so the URL is
//   captured at build time rather than hard-coded here. Currently the
//   fallback below points at the live execute-api endpoint so prod
//   still works even without the env var; remove the fallback once the
//   build script sets it explicitly (or a custom domain is configured).
const LLM_BILLING_API_BASE_URL = process.env.REACT_APP_LLM_BILLING_API_URL
  || (process.env.NODE_ENV === 'production'
    ? 'https://6acojbxmj3.execute-api.ap-southeast-2.amazonaws.com/prod'
    : API_BASE_URL);
const llmBillingApi = new ApiClient(LLM_BILLING_API_BASE_URL);

// The repo↔agent LINK CRUD routes (/github/links + /gitlab/links) live on the
// dedicated Webhooks RestApi (WebhooksApiNestedStack), NOT the main api-prod
// RestApi — same split as llmBilling above (MainApiRoutes is at the 500-resource
// CFN cap). Prod has a custom domain (webhooks-api-prod.zibby.app); local dev
// reuses the same Express server on :3001, so it falls back to API_BASE_URL when
// the env override is unset. Set REACT_APP_WEBHOOKS_API_URL at build time for
// prod if/when a non-prod stage needs to point elsewhere.
const WEBHOOKS_API_BASE_URL = process.env.REACT_APP_WEBHOOKS_API_URL
  || (process.env.NODE_ENV === 'production'
    ? 'https://webhooks-api-prod.zibby.app'
    : API_BASE_URL);
const webhooksApi = new ApiClient(WEBHOOKS_API_BASE_URL);

const ACCOUNT_ENDPOINTS = {
  TOKENS: '/api/user/tokens',
  TOKEN: (tokenId) => `/api/user/tokens/${tokenId}`,
  EXECUTIONS_BULK_DELETE: '/executions/bulk',
};

export default api;
export {
  API_BASE_URL,
  ACCOUNT_API_BASE_URL,
  LLM_BILLING_API_BASE_URL,
  WEBHOOKS_API_BASE_URL,
  getAuthToken,
  accountApi,
  llmBillingApi,
  webhooksApi,
  ApiClientFactory,
  ACCOUNT_ENDPOINTS,
};
